import { getChatToken } from '../http/account';

interface ZendeskConfig {
  token: string;
  isLoaded: boolean;
  isLoading: boolean;
}

class ZendeskLoader {
  private config: ZendeskConfig = {
    token: '',
    isLoaded: false,
    isLoading: false
  };

  private loadPromise: Promise<void> | null = null;

  private async loadZendeskScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.config.isLoaded && window.zE) {
        resolve();
        return;
      }

      if (this.config.isLoading) {
        if (this.loadPromise) {
          this.loadPromise.then(resolve).catch(reject);
        }
        return;
      }

      this.config.isLoading = true;

      const script = document.createElement('script');
      script.id = 'ze-snippet';
      script.src = `https://static.zdassets.com/ekr/snippet.js?key=${process.env.VUE_APP_ZENDESK_TOKEN}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.config.isLoaded = true;
        this.config.isLoading = false;
        this.initializeZendesk();
        resolve();
      };

      script.onerror = () => {
        this.config.isLoading = false;
        reject(new Error('Falha ao carregar script do Zendesk'));
      };

      document.head.appendChild(script);
    });
  }

  private initializeZendesk(): void {
    if (!window.zE) return;

    let isOpen = false;

    window.zE('messenger:on', 'close', function () {
      window.zE('messenger', 'hide');
      isOpen = false;
    });

    window.zE('messenger', 'hide');

    window.openChat = () => {
      isOpen = !isOpen;
      window.zE('messenger', isOpen ? 'show' : 'hide');
      window.zE('messenger', isOpen ? 'open' : 'close');
    };
  }

  private async authenticateUser(): Promise<void> {
    try {
      const { token } = await getChatToken();
      this.config.token = token;

      if (window.zE) {
        window.zE(
          'messenger',
          'loginUser',
          function (callback: (token: string) => void) {
            callback(token);
          }
        );
      }
    } catch (error) {
      console.error('Não foi possível autenticar o usuário no Zendesk:', error);
      throw error;
    }
  }

  async loadAndAuthenticate(): Promise<void> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this.performLoadAndAuthenticate();
    return this.loadPromise;
  }

  private async performLoadAndAuthenticate(): Promise<void> {
    try {
      await this.loadZendeskScript();
      await this.authenticateUser();
    } catch (error) {
      this.loadPromise = null;
      throw error;
    }
  }

  async openChat(): Promise<void> {
    try {
      await this.loadAndAuthenticate();

      if (window.openChat) {
        window.openChat();
      } else {
        throw new Error('Função openChat não disponível');
      }
    } catch (error) {
      console.error('Erro ao abrir chat do Zendesk:', error);
      throw error;
    }
  }

  isLoaded(): boolean {
    return this.config.isLoaded && !!window.zE;
  }

  isLoading(): boolean {
    return this.config.isLoading;
  }
}

export const zendeskLoader = new ZendeskLoader();

declare global {
  interface Window {
    zE: any;
    openChat: () => void;
  }
}
